import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Receipt, Tag, TrendingUp, Download, Plus, Pencil,
  Trash2, Search, Eye, Users, DollarSign, CreditCard,
  BarChart3, Loader2, Check, X, Copy, FileText, ChevronDown, ChevronUp, ChevronRight, Mail, CalendarClock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Milestone schedule for contributor thank-you coupons. Must stay in sync
// with /api/admin/send-coupon-reminders on the backend.
const REMINDER_MILESTONES = [
  { key: '3m', label: '3 meses', daysFromCreation: 90 },
  { key: '5m', label: '5 meses', daysFromCreation: 150 },
  { key: '1w', label: '1 semana antes de caducar', daysBeforeExpiry: 7 },
];

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return String(iso).slice(0, 10);
  }
};

const addDays = (isoDate, days) => {
  const d = new Date(isoDate);
  d.setDate(d.getDate() + days);
  return d.toISOString();
};

const ReminderSchedule = ({ coupon }) => {
  if (coupon.source !== 'contributor_thank_you') return null;

  const createdAt = coupon.created_at;
  const expiresAt = coupon.expires_at;
  const claimedAt = coupon.claimed_at;
  const assigned = Boolean(coupon.assigned_to);
  const sentList = coupon.reminders_sent || [];
  const now = new Date();

  const items = REMINDER_MILESTONES.map((m) => {
    let scheduledIso = null;
    if (m.daysFromCreation && createdAt) {
      scheduledIso = addDays(createdAt, m.daysFromCreation);
    } else if (m.daysBeforeExpiry && expiresAt) {
      scheduledIso = addDays(expiresAt, -m.daysBeforeExpiry);
    }
    const sent = sentList.includes(m.key);
    const sentAt = coupon[`reminder_${m.key}_sent_at`];
    const scheduledDate = scheduledIso ? new Date(scheduledIso) : null;
    const isFuture = scheduledDate && scheduledDate > now;
    let status = 'pending';
    if (sent) status = 'sent';
    else if (!assigned) status = 'waiting_registration';
    else if (isFuture) status = 'scheduled';
    else status = 'due'; // date reached but not sent yet (cron pending or user created a book)
    return { ...m, scheduledIso, sent, sentAt, status };
  });

  const STATUS_STYLES = {
    sent:                 { bg: 'bg-emerald-50',  border: 'border-emerald-300', text: 'text-emerald-800',  badge: 'Enviado' },
    scheduled:            { bg: 'bg-blue-50',     border: 'border-blue-300',    text: 'text-blue-800',     badge: 'Programado' },
    due:                  { bg: 'bg-amber-50',    border: 'border-amber-300',   text: 'text-amber-800',    badge: 'Pendiente de envio' },
    waiting_registration: { bg: 'bg-slate-50',    border: 'border-slate-300',   text: 'text-slate-600',    badge: 'Esperando registro' },
    pending:              { bg: 'bg-slate-50',    border: 'border-slate-200',   text: 'text-slate-600',    badge: 'Pendiente' },
  };

  return (
    <div className="mt-3 border rounded-md bg-muted/40 p-3" data-testid={`coupon-reminders-${coupon.coupon_id}`}>
      <div className="flex items-center gap-2 text-xs font-semibold text-slate-700 mb-2">
        <CalendarClock className="w-3.5 h-3.5" />
        Calendario de recordatorios al cliente
      </div>
      {!assigned && (
        <p className="text-[11px] text-slate-500 mb-2 italic">
          Los recordatorios solo se envian si el colaborador se registra como usuario. Mientras tanto
          se muestran las fechas previstas segun la fecha de emision del cupon.
        </p>
      )}
      <div className="grid sm:grid-cols-3 gap-2">
        {items.map((it) => {
          const s = STATUS_STYLES[it.status];
          return (
            <div
              key={it.key}
              className={`rounded-md border ${s.border} ${s.bg} p-2.5`}
              data-testid={`coupon-reminder-${coupon.coupon_id}-${it.key}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className={`text-[11px] font-semibold ${s.text}`}>{it.label}</span>
                <Badge variant="outline" className="text-[9px] font-normal">
                  {s.badge}
                </Badge>
              </div>
              <div className="mt-1 flex items-start gap-1.5 text-[11px] text-slate-700">
                <Mail className="w-3 h-3 mt-0.5 flex-shrink-0 opacity-60" />
                <span>
                  {it.sent
                    ? `Enviado el ${formatDate(it.sentAt)}`
                    : `Previsto ${formatDate(it.scheduledIso)}`}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500">
        <span>Emitido: <strong>{formatDate(createdAt)}</strong></span>
        {claimedAt && <span>Reclamado: <strong>{formatDate(claimedAt)}</strong></span>}
        <span>Caduca: <strong>{formatDate(expiresAt)}</strong></span>
      </div>
    </div>
  );
};

const COUPON_TYPES = [
  { value: 'percentage', label: 'Porcentaje (%)' },
  { value: 'fixed', label: 'Importe fijo (€)' },
  { value: 'free_book', label: 'Libro gratis' },
  { value: 'free_extra', label: 'Ejemplar extra gratis' },
];

// Visual metadata for grouping coupons by ``type`` in the admin panel.
// Order here drives the rendering order of the sections; empty groups
// are skipped so the panel stays clean when only one or two types
// exist (which is the common case).
const COUPON_TYPE_GROUPS = [
  { value: 'percentage', label: 'Porcentaje', headerClass: 'text-violet-700',  badgeBg: 'bg-violet-100 text-violet-700' },
  { value: 'fixed',      label: 'Importe fijo (€)', headerClass: 'text-emerald-700', badgeBg: 'bg-emerald-100 text-emerald-700' },
  { value: 'free_book',  label: 'Libro gratis', headerClass: 'text-amber-700',  badgeBg: 'bg-amber-100 text-amber-700' },
  { value: 'free_extra', label: 'Ejemplar extra gratis', headerClass: 'text-sky-700', badgeBg: 'bg-sky-100 text-sky-700' },
];

const formatCouponValue = (coupon) => {
  if (coupon.type === 'percentage') return `${coupon.value}%`;
  if (coupon.type === 'fixed')      return `${coupon.value}€`;
  if (coupon.type === 'free_book')  return 'Libro gratis';
  return 'Extra gratis';
};

export const AdminBillingPanel = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState('all');
  const [couponDialogOpen, setCouponDialogOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [couponForm, setCouponForm] = useState({
    code: '', type: 'percentage', value: 0, description: '',
    max_uses: '', expires_at: '', assigned_to: '', assigned_email: '',
  });
  const [savingCoupon, setSavingCoupon] = useState(false);
  const [expandedCoupons, setExpandedCoupons] = useState(new Set());
  // Bulk-action state: ``selectedCoupons`` holds the coupon_ids of every
  // coupon the admin has ticked across the (filtered) groups. The bulk
  // action bar slides in at the top of the panel as soon as it has any
  // items in it.
  const [selectedCoupons, setSelectedCoupons] = useState(new Set());
  const [bulkExpiryOpen, setBulkExpiryOpen] = useState(false);
  const [bulkExpiryDate, setBulkExpiryDate] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  // Per-group collapse state. ``true`` means COLLAPSED. Groups start
  // expanded, so an empty set = all expanded.
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  // Coupon filters: email search, status (active/expired/all), origin
  // book (book_id or 'all'/'manual').
  const [couponEmailQuery, setCouponEmailQuery] = useState('');
  const [couponStatusFilter, setCouponStatusFilter] = useState('all');
  const [couponOriginFilter, setCouponOriginFilter] = useState('all');

  const toggleGroupCollapsed = (groupValue) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupValue)) next.delete(groupValue);
      else next.add(groupValue);
      return next;
    });
  };

  const allGroupsCollapsed = (visibleGroups) =>
    visibleGroups.every((g) => collapsedGroups.has(g));

  const toggleAllGroups = (visibleGroups) => {
    if (allGroupsCollapsed(visibleGroups)) {
      // Expand all visible groups.
      setCollapsedGroups((prev) => {
        const next = new Set(prev);
        visibleGroups.forEach((g) => next.delete(g));
        return next;
      });
    } else {
      // Collapse all visible groups.
      setCollapsedGroups((prev) => {
        const next = new Set(prev);
        visibleGroups.forEach((g) => next.add(g));
        return next;
      });
    }
  };

  // ---- Bulk selection helpers ----
  const toggleSelectCoupon = (couponId) => {
    setSelectedCoupons((prev) => {
      const next = new Set(prev);
      if (next.has(couponId)) next.delete(couponId);
      else next.add(couponId);
      return next;
    });
  };

  const selectAllInList = (couponIds) => {
    // If ALL ids in ``couponIds`` are already selected, deselect them.
    // Otherwise, add the missing ones (idempotent toggle for group-level
    // and global "select all").
    setSelectedCoupons((prev) => {
      const next = new Set(prev);
      const allSelected = couponIds.every((id) => next.has(id));
      if (allSelected) {
        couponIds.forEach((id) => next.delete(id));
      } else {
        couponIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedCoupons(new Set());

  const bulkDeleteSelected = async () => {
    const ids = Array.from(selectedCoupons);
    if (ids.length === 0) return;
    if (!window.confirm(`¿Eliminar ${ids.length} cupón(es) seleccionado(s)? Esta acción NO se puede deshacer.`)) return;
    setBulkBusy(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/coupons/bulk-delete`,
        { coupon_ids: ids },
        { withCredentials: true },
      );
      toast.success(`${res.data.deleted_count} cupón(es) eliminado(s)`);
      clearSelection();
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudieron eliminar');
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkUpdateExpiry = async () => {
    const ids = Array.from(selectedCoupons);
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/coupons/bulk-update-expiry`,
        { coupon_ids: ids, expires_at: bulkExpiryDate || null },
        { withCredentials: true },
      );
      toast.success(
        bulkExpiryDate
          ? `Caducidad actualizada para ${res.data.modified_count} cupón(es)`
          : `Caducidad eliminada de ${res.data.modified_count} cupón(es)`,
      );
      setBulkExpiryOpen(false);
      setBulkExpiryDate('');
      clearSelection();
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo actualizar la caducidad');
    } finally {
      setBulkBusy(false);
    }
  };

  const toggleCouponExpanded = (couponId) => {
    setExpandedCoupons((prev) => {
      const next = new Set(prev);
      if (next.has(couponId)) next.delete(couponId);
      else next.add(couponId);
      return next;
    });
  };
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({
    user_id: '', invoice_type: 'manual', status: 'pending', notes: '',
    items: [{ description: '', quantity: 1, unit_price: 0 }],
  });
  const [savingInvoice, setSavingInvoice] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusInvoice, setStatusInvoice] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewNumber, setPreviewNumber] = useState('');
  const [editNumberDialog, setEditNumberDialog] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [editNumber, setEditNumber] = useState('');

  const fetchAll = async () => {
    try {
      const [statsRes, invoicesRes, couponsRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/billing-stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/invoices`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/coupons`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/users`, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setInvoices(invoicesRes.data);
      setCoupons(couponsRes.data);
      setUsers(usersRes.data.filter(u => u.role === 'client'));
    } catch (err) {
      toast.error('Error cargando datos de facturacion');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // --- INVOICE HELPERS ---
  const filteredInvoices = invoices.filter(inv => {
    const matchSearch = !invoiceSearch || 
      inv.invoice_number?.toLowerCase().includes(invoiceSearch.toLowerCase()) ||
      inv.billing_profile?.name?.toLowerCase().includes(invoiceSearch.toLowerCase()) ||
      inv.billing_profile?.email?.toLowerCase().includes(invoiceSearch.toLowerCase());
    const matchStatus = invoiceStatusFilter === 'all' || inv.status === invoiceStatusFilter;
    return matchSearch && matchStatus;
  });

  const downloadInvoicePdf = async (invoiceId, invoiceNumber) => {
    try {
      const res = await axios.get(`${API_URL}/api/billing/invoices/${invoiceId}/pdf`, {
        withCredentials: true, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `factura_${invoiceNumber}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Error al descargar PDF'); }
  };

  const previewInvoicePdf = async (invoiceId, invoiceNumber) => {
    try {
      const res = await axios.get(`${API_URL}/api/billing/invoices/${invoiceId}/pdf`, {
        withCredentials: true, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      setPreviewUrl(url);
      setPreviewNumber(invoiceNumber);
    } catch { toast.error('Error al cargar la factura'); }
  };

  const closePreview = () => {
    if (previewUrl) window.URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewNumber('');
  };

  const deleteInvoice = async (inv) => {
    if (!window.confirm(`¿Eliminar la factura ${inv.invoice_number}? Esta accion no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/invoices/${inv.invoice_id}`, { withCredentials: true });
      toast.success('Factura eliminada');
      fetchAll();
    } catch { toast.error('Error al eliminar factura'); }
  };

  const saveInvoiceNumber = async () => {
    if (!editingInvoice || !editNumber.trim()) return;
    try {
      await axios.put(`${API_URL}/api/admin/invoices/${editingInvoice.invoice_id}`,
        { invoice_number: editNumber.trim() }, { withCredentials: true });
      toast.success('Numero de factura actualizado');
      setEditNumberDialog(false);
      setEditingInvoice(null);
      fetchAll();
    } catch { toast.error('Error al actualizar numero'); }
  };

  const changeInvoiceStatus = async () => {
    if (!statusInvoice || !newStatus) return;
    try {
      await axios.put(`${API_URL}/api/admin/invoices/${statusInvoice.invoice_id}`, 
        { status: newStatus }, { withCredentials: true });
      toast.success('Estado de factura actualizado');
      setStatusDialogOpen(false);
      fetchAll();
    } catch { toast.error('Error al actualizar estado'); }
  };

  const createManualInvoice = async () => {
    if (!invoiceForm.user_id || invoiceForm.items.every(i => !i.description)) {
      toast.error('Completa los campos requeridos');
      return;
    }
    setSavingInvoice(true);
    try {
      const lineItems = invoiceForm.items
        .filter(i => i.description)
        .map(i => ({
          description: i.description,
          quantity: parseInt(i.quantity) || 1,
          unit_price: parseFloat(i.unit_price) || 0,
          amount: (parseInt(i.quantity) || 1) * (parseFloat(i.unit_price) || 0),
        }));
      await axios.post(`${API_URL}/api/admin/invoices`, {
        user_id: invoiceForm.user_id,
        invoice_type: invoiceForm.invoice_type,
        status: invoiceForm.status,
        notes: invoiceForm.notes,
        line_items: lineItems,
      }, { withCredentials: true });
      toast.success('Factura creada correctamente');
      setInvoiceDialogOpen(false);
      setInvoiceForm({ user_id: '', invoice_type: 'manual', status: 'pending', notes: '', items: [{ description: '', quantity: 1, unit_price: 0 }] });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear factura');
    } finally { setSavingInvoice(false); }
  };

  // --- COUPON HELPERS ---
  const openNewCoupon = () => {
    setEditingCoupon(null);
    setCouponForm({ code: '', type: 'percentage', value: 0, description: '', max_uses: '', expires_at: '', assigned_to: '', assigned_email: '' });
    setCouponDialogOpen(true);
  };

  const openEditCoupon = (coupon) => {
    setEditingCoupon(coupon);
    setCouponForm({
      code: coupon.code,
      type: coupon.type,
      value: coupon.value || 0,
      description: coupon.description || '',
      max_uses: coupon.max_uses || '',
      expires_at: coupon.expires_at ? coupon.expires_at.slice(0, 10) : '',
      assigned_to: coupon.assigned_to || '',
      assigned_email: coupon.assigned_email || '',
    });
    setCouponDialogOpen(true);
  };

  const saveCoupon = async () => {
    if (!couponForm.code.trim()) { toast.error('El codigo es obligatorio'); return; }
    setSavingCoupon(true);
    try {
      const payload = {
        code: couponForm.code,
        type: couponForm.type,
        value: parseFloat(couponForm.value) || 0,
        description: couponForm.description,
        max_uses: couponForm.max_uses ? parseInt(couponForm.max_uses) : null,
        expires_at: couponForm.expires_at || null,
        assigned_to: couponForm.assigned_to || null,
        // Email-bound coupon. When set, ONLY the user logged in with this
        // exact email can redeem the code — used for personalised codes
        // sent to contributors.
        assigned_email: (couponForm.assigned_email || '').trim() || null,
      };
      if (editingCoupon) {
        await axios.put(`${API_URL}/api/admin/coupons/${editingCoupon.coupon_id}`, payload, { withCredentials: true });
        toast.success('Cupon actualizado');
      } else {
        await axios.post(`${API_URL}/api/admin/coupons`, payload, { withCredentials: true });
        toast.success('Cupon creado');
      }
      setCouponDialogOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar cupon');
    } finally { setSavingCoupon(false); }
  };

  const toggleCoupon = async (coupon) => {
    try {
      await axios.put(`${API_URL}/api/admin/coupons/${coupon.coupon_id}`, 
        { is_active: !coupon.is_active }, { withCredentials: true });
      toast.success(coupon.is_active ? 'Cupon desactivado' : 'Cupon activado');
      fetchAll();
    } catch { toast.error('Error al cambiar estado'); }
  };

  const deleteCoupon = async (coupon) => {
    if (!window.confirm(`¿Eliminar el cupon ${coupon.code}?`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/coupons/${coupon.coupon_id}`, { withCredentials: true });
      toast.success('Cupon eliminado');
      fetchAll();
    } catch { toast.error('Error al eliminar cupon'); }
  };

  const getUserName = (userId) => {
    const u = users.find(u => u.user_id === userId);
    return u ? `${u.name} (${u.email})` : userId || '—';
  };

  const getUserEmail = (userId) => {
    const u = users.find(u => u.user_id === userId);
    return u ? u.email : '';
  };

  if (loading) return (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="w-6 h-6 animate-spin text-primary" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-billing-panel">
      {/* KPIs */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="billing-kpis">
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                <TrendingUp className="w-3.5 h-3.5" /> Total facturado
              </div>
              <p className="text-xl font-bold text-green-700" data-testid="kpi-total-revenue">{stats.total_revenue.toFixed(2)}€</p>
              <p className="text-[10px] text-muted-foreground">{stats.total_invoices} facturas</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                <BarChart3 className="w-3.5 h-3.5" /> Este mes
              </div>
              <p className="text-xl font-bold text-blue-700" data-testid="kpi-monthly-revenue">{stats.monthly_revenue.toFixed(2)}€</p>
              <p className="text-[10px] text-muted-foreground">{stats.monthly_invoices} facturas</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                <CreditCard className="w-3.5 h-3.5" /> Pendientes
              </div>
              <p className="text-xl font-bold text-amber-700" data-testid="kpi-pending">{stats.pending_count}</p>
              <p className="text-[10px] text-muted-foreground">{stats.pending_total.toFixed(2)}€</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                <Tag className="w-3.5 h-3.5" /> Cupones activos
              </div>
              <p className="text-xl font-bold text-purple-700" data-testid="kpi-active-coupons">{stats.active_coupons}</p>
              <p className="text-[10px] text-muted-foreground">{stats.total_coupon_uses} usos totales</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-red-400">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                <DollarSign className="w-3.5 h-3.5" /> Descuentos
              </div>
              <p className="text-xl font-bold text-red-600" data-testid="kpi-discounts">-{stats.total_discounts.toFixed(2)}€</p>
              <p className="text-[10px] text-muted-foreground">{stats.total_coupons} cupones</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Sub-tabs: Facturas / Cupones */}
      <Tabs defaultValue="invoices" data-testid="billing-subtabs">
        <TabsList>
          <TabsTrigger value="invoices" className="gap-2" data-testid="subtab-invoices">
            <Receipt className="w-4 h-4" /> Facturas ({invoices.length})
          </TabsTrigger>
          <TabsTrigger value="coupons" className="gap-2" data-testid="subtab-coupons">
            <Tag className="w-4 h-4" /> Cupones ({coupons.length})
          </TabsTrigger>
        </TabsList>

        {/* === INVOICES TAB === */}
        <TabsContent value="invoices">
          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <CardTitle className="text-lg">Gestion de facturas</CardTitle>
                <div className="flex gap-2">
                  <div className="relative flex-1 md:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      value={invoiceSearch}
                      onChange={(e) => setInvoiceSearch(e.target.value)}
                      placeholder="Buscar por numero, nombre o email..."
                      className="pl-9 h-9 text-sm"
                      data-testid="invoice-search"
                    />
                  </div>
                  <Select value={invoiceStatusFilter} onValueChange={setInvoiceStatusFilter}>
                    <SelectTrigger className="w-[130px] h-9" data-testid="invoice-filter-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      <SelectItem value="paid">Pagadas</SelectItem>
                      <SelectItem value="pending">Pendientes</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button size="sm" className="gap-1" onClick={() => {
                    setInvoiceForm({ user_id: '', invoice_type: 'manual', status: 'pending', notes: '', items: [{ description: '', quantity: 1, unit_price: 0 }] });
                    setInvoiceDialogOpen(true);
                  }} data-testid="create-invoice-btn">
                    <Plus className="w-4 h-4" /> Nueva factura
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {filteredInvoices.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Receipt className="w-10 h-10 mx-auto mb-2 opacity-40" />
                  <p>No hay facturas</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-muted/30 text-xs">
                        <th className="text-left py-2.5 px-3 font-medium text-muted-foreground">N° Factura</th>
                        <th className="text-left py-2.5 px-3 font-medium text-muted-foreground">Cliente</th>
                        <th className="text-left py-2.5 px-3 font-medium text-muted-foreground">Fecha</th>
                        <th className="text-left py-2.5 px-3 font-medium text-muted-foreground">Tipo</th>
                        <th className="text-right py-2.5 px-3 font-medium text-muted-foreground">Subtotal</th>
                        <th className="text-right py-2.5 px-3 font-medium text-muted-foreground">Dto.</th>
                        <th className="text-right py-2.5 px-3 font-medium text-muted-foreground">IVA</th>
                        <th className="text-right py-2.5 px-3 font-medium text-muted-foreground">Total</th>
                        <th className="text-center py-2.5 px-3 font-medium text-muted-foreground">Estado</th>
                        <th className="text-right py-2.5 px-3 font-medium text-muted-foreground">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredInvoices.map((inv) => (
                        <tr key={inv.invoice_id} className="border-b last:border-0 hover:bg-muted/30 transition-colors text-sm" data-testid={`admin-inv-${inv.invoice_id}`}>
                          <td className="py-2.5 px-3">
                            <button
                              onClick={() => { setEditingInvoice(inv); setEditNumber(inv.invoice_number); setEditNumberDialog(true); }}
                              className="font-medium hover:text-primary transition-colors flex items-center gap-1 group"
                              data-testid={`inv-number-${inv.invoice_id}`}
                            >
                              {inv.invoice_number}
                              <Pencil className="w-2.5 h-2.5 opacity-0 group-hover:opacity-50 transition-opacity" />
                            </button>
                          </td>
                          <td className="py-2.5 px-3">
                            <span className="text-xs">{inv.billing_profile?.name || '—'}</span>
                            <span className="block text-[10px] text-muted-foreground">{inv.billing_profile?.email || ''}</span>
                          </td>
                          <td className="py-2.5 px-3 text-muted-foreground text-xs">{new Date(inv.created_at).toLocaleDateString()}</td>
                          <td className="py-2.5 px-3">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                              inv.invoice_type === 'initial' ? 'bg-blue-100 text-blue-700' :
                              inv.invoice_type === 'extras' ? 'bg-purple-100 text-purple-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {inv.invoice_type === 'initial' ? 'Libro' : inv.invoice_type === 'extras' ? 'Extras' : 'Manual'}
                            </span>
                          </td>
                          <td className="py-2.5 px-3 text-right text-xs">{inv.subtotal?.toFixed(2)}€</td>
                          <td className="py-2.5 px-3 text-right text-xs text-red-600">{inv.discount > 0 ? `-${inv.discount.toFixed(2)}€` : '—'}</td>
                          <td className="py-2.5 px-3 text-right text-xs text-muted-foreground">{inv.tax?.toFixed(2)}€</td>
                          <td className="py-2.5 px-3 text-right font-semibold">{inv.total?.toFixed(2)}€</td>
                          <td className="py-2.5 px-3 text-center">
                            <button
                              onClick={() => { setStatusInvoice(inv); setNewStatus(inv.status); setStatusDialogOpen(true); }}
                              className="cursor-pointer"
                              data-testid={`inv-status-${inv.invoice_id}`}
                            >
                              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                                inv.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                              }`}>
                                {inv.status === 'paid' ? 'Pagada' : 'Pendiente'}
                              </span>
                            </button>
                          </td>
                          <td className="py-2.5 px-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs gap-1"
                                onClick={() => previewInvoicePdf(inv.invoice_id, inv.invoice_number)}
                                data-testid={`inv-preview-${inv.invoice_id}`}
                              >
                                <Eye className="w-3 h-3" /> Ver
                              </Button>
                              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs gap-1"
                                onClick={() => downloadInvoicePdf(inv.invoice_id, inv.invoice_number)}
                                data-testid={`inv-download-${inv.invoice_id}`}
                              >
                                <Download className="w-3 h-3" />
                              </Button>
                              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-red-500 hover:text-red-700 gap-1"
                                onClick={() => deleteInvoice(inv)}
                                data-testid={`inv-delete-${inv.invoice_id}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* === COUPONS TAB === */}
        <TabsContent value="coupons">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Gestion de cupones</CardTitle>
                <Button size="sm" className="gap-1" onClick={openNewCoupon} data-testid="create-coupon-btn">
                  <Plus className="w-4 h-4" /> Nuevo cupon
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {coupons.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Tag className="w-10 h-10 mx-auto mb-2 opacity-40" />
                  <p>No hay cupones</p>
                </div>
              ) : (
                (() => {
                  // ---- Apply user filters (email search + status + origin) ----
                  const now = new Date();
                  const q = couponEmailQuery.trim().toLowerCase();
                  const filtered = coupons.filter((c) => {
                    // Email filter — matches assigned_email OR (when missing)
                    // the assigned_to user's email via the cached map.
                    if (q) {
                      const haystack = (
                        (c.assigned_email || '') +
                        ' ' +
                        (c.assigned_to ? getUserEmail(c.assigned_to) || '' : '')
                      ).toLowerCase();
                      if (!haystack.includes(q)) return false;
                    }
                    // Status filter — expired vs active.
                    if (couponStatusFilter !== 'all') {
                      const expired = c.expires_at && new Date(c.expires_at) < now;
                      if (couponStatusFilter === 'expired' && !expired) return false;
                      if (couponStatusFilter === 'active' && (expired || c.is_active === false)) return false;
                    }
                    // Origin filter — by source_book_id, or 'manual' for
                    // coupons not originated from a contribution.
                    if (couponOriginFilter !== 'all') {
                      if (couponOriginFilter === 'manual') {
                        if (c.source_book_id) return false;
                      } else if (c.source_book_id !== couponOriginFilter) {
                        return false;
                      }
                    }
                    return true;
                  });

                  // Distinct origin books (only those that actually have coupons).
                  const originOptions = Array.from(
                    coupons.reduce((map, c) => {
                      if (c.source_book_id && !map.has(c.source_book_id)) {
                        map.set(
                          c.source_book_id,
                          c.source_book_title || c.source_book_id,
                        );
                      }
                      return map;
                    }, new Map()),
                  );

                  // Group filtered coupons by their ``type``. Empty groups
                  // are hidden so the admin only sees what actually exists.
                  const visibleGroups = COUPON_TYPE_GROUPS
                    .filter((g) => filtered.some((c) => c.type === g.value))
                    .map((g) => g.value);
                  const allCollapsed = visibleGroups.length > 0 && allGroupsCollapsed(visibleGroups);

                  return (
                    <div className="space-y-4">
                      {/* ====== Bulk action bar (sticky-feel when ≥1 selected) ====== */}
                      {selectedCoupons.size > 0 && (
                        <div
                          className="flex flex-wrap items-center gap-2 p-3 rounded-md bg-violet-50 border border-violet-200"
                          data-testid="bulk-action-bar"
                        >
                          <span className="text-sm font-medium text-violet-900" data-testid="bulk-selection-count">
                            {selectedCoupons.size} seleccionado{selectedCoupons.size === 1 ? '' : 's'}
                          </span>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 gap-1 text-xs"
                            onClick={() => { setBulkExpiryDate(''); setBulkExpiryOpen(v => !v); }}
                            data-testid="bulk-expiry-btn"
                            disabled={bulkBusy}
                          >
                            <CalendarClock className="w-3.5 h-3.5" /> Cambiar caducidad
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 gap-1 text-xs border-rose-300 text-rose-700 hover:bg-rose-50"
                            onClick={bulkDeleteSelected}
                            data-testid="bulk-delete-btn"
                            disabled={bulkBusy}
                          >
                            {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                            Eliminar
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 text-xs ml-auto"
                            onClick={clearSelection}
                            data-testid="bulk-clear-btn"
                            disabled={bulkBusy}
                          >
                            Cancelar selección
                          </Button>
                        </div>
                      )}
                      {bulkExpiryOpen && selectedCoupons.size > 0 && (
                        <div className="flex flex-wrap items-end gap-2 p-3 rounded-md bg-violet-50/70 border border-violet-200" data-testid="bulk-expiry-form">
                          <div className="flex-1 min-w-[200px]">
                            <Label className="text-xs">Nueva fecha de caducidad</Label>
                            <Input
                              type="date"
                              value={bulkExpiryDate}
                              onChange={(e) => setBulkExpiryDate(e.target.value)}
                              className="h-9 text-sm"
                              data-testid="bulk-expiry-date-input"
                            />
                            <p className="text-[11px] text-muted-foreground mt-1">
                              Dejar vacío y aplicar = eliminar la fecha de caducidad (cupón sin vencimiento).
                            </p>
                          </div>
                          <Button
                            size="sm"
                            onClick={bulkUpdateExpiry}
                            disabled={bulkBusy}
                            className="h-9 gap-1 text-xs"
                            data-testid="bulk-expiry-apply"
                          >
                            {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                            Aplicar a {selectedCoupons.size} cupón(es)
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setBulkExpiryOpen(false)}
                            className="h-9 text-xs"
                          >
                            Cancelar
                          </Button>
                        </div>
                      )}

                      {/* ====== Filter bar ====== */}
                      <div className="flex flex-wrap gap-2 items-center pb-3 border-b" data-testid="coupon-filters">
                        <div className="relative flex-1 min-w-[200px]">
                          <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted-foreground pointer-events-none" />
                          <Input
                            placeholder="Buscar por email…"
                            value={couponEmailQuery}
                            onChange={(e) => setCouponEmailQuery(e.target.value)}
                            className="pl-8 h-9 text-sm"
                            data-testid="coupon-email-search"
                          />
                        </div>
                        <Select value={couponStatusFilter} onValueChange={setCouponStatusFilter}>
                          <SelectTrigger className="w-[140px] h-9 text-xs" data-testid="coupon-status-filter">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Todos</SelectItem>
                            <SelectItem value="active">Activos</SelectItem>
                            <SelectItem value="expired">Caducados</SelectItem>
                          </SelectContent>
                        </Select>
                        <Select value={couponOriginFilter} onValueChange={setCouponOriginFilter}>
                          <SelectTrigger className="w-[200px] h-9 text-xs" data-testid="coupon-origin-filter">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Todos los orígenes</SelectItem>
                            <SelectItem value="manual">Manuales (sin origen)</SelectItem>
                            {originOptions.map(([id, title]) => (
                              <SelectItem key={id} value={id}>
                                {title.length > 35 ? `${title.slice(0, 35)}…` : title}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {visibleGroups.length > 0 && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => toggleAllGroups(visibleGroups)}
                            className="h-9 gap-1 text-xs"
                            data-testid="toggle-all-groups-btn"
                          >
                            {allCollapsed ? (
                              <>
                                <ChevronDown className="w-3.5 h-3.5" /> Expandir todos
                              </>
                            ) : (
                              <>
                                <ChevronUp className="w-3.5 h-3.5" /> Contraer todos
                              </>
                            )}
                          </Button>
                        )}
                        <span className="text-xs text-muted-foreground ml-auto" data-testid="coupon-filter-counter">
                          {filtered.length} de {coupons.length}
                        </span>
                      </div>

                      {filtered.length === 0 ? (
                        <p className="text-center py-6 text-sm text-muted-foreground italic">
                          Ningún cupón coincide con los filtros actuales.
                        </p>
                      ) : (
                      <div className="space-y-8" data-testid="coupons-grouped-by-type">
                        {COUPON_TYPE_GROUPS.map((group) => {
                          const groupCoupons = filtered.filter((c) => c.type === group.value);
                          if (groupCoupons.length === 0) return null;
                          const isCollapsed = collapsedGroups.has(group.value);
                          const groupIds = groupCoupons.map((c) => c.coupon_id);
                          const allInGroupSelected = groupIds.every((id) => selectedCoupons.has(id));
                          return (
                            <div key={group.value} data-testid={`coupon-group-${group.value}`}>
                              <div className="flex items-center gap-2 mb-3 pb-2 border-b">
                                <input
                                  type="checkbox"
                                  className="w-4 h-4 accent-violet-600 cursor-pointer"
                                  checked={allInGroupSelected}
                                  onChange={() => selectAllInList(groupIds)}
                                  onClick={(e) => e.stopPropagation()}
                                  title={allInGroupSelected ? 'Deseleccionar todos de este grupo' : 'Seleccionar todos de este grupo'}
                                  data-testid={`coupon-group-select-all-${group.value}`}
                                />
                                <button
                                  type="button"
                                  onClick={() => toggleGroupCollapsed(group.value)}
                                  className="flex-1 flex items-center gap-2 text-left hover:opacity-80 transition-opacity"
                                  data-testid={`coupon-group-toggle-${group.value}`}
                                >
                                  {isCollapsed ? (
                                    <ChevronRight className={`w-4 h-4 ${group.headerClass}`} />
                                  ) : (
                                    <ChevronDown className={`w-4 h-4 ${group.headerClass}`} />
                                  )}
                                  <Tag className={`w-4 h-4 ${group.headerClass}`} />
                                  <h3 className={`text-sm font-semibold ${group.headerClass}`}>{group.label}</h3>
                                  <Badge variant="outline" className={`text-[10px] ${group.badgeBg} border-0`}>
                                    {groupCoupons.length}
                                  </Badge>
                                </button>
                              </div>
                              {!isCollapsed && (
                              <div className="space-y-3">
                                {groupCoupons.map((coupon) => {
                                // Contributor thank-you coupons render with an
                                // expandable reminder calendar; other coupons
                                // (manual/admin-created) render the compact card.
                                const isThankYou = coupon.source === 'contributor_thank_you';
                                const isExpanded = expandedCoupons.has(coupon.coupon_id);
                                const collaboratorEmail = coupon.assigned_email
                                  || (coupon.assigned_to ? getUserEmail(coupon.assigned_to) : '');
                                if (!isThankYou) {
                                  return (
                                    <div
                                      key={coupon.coupon_id}
                                      className={`flex items-center gap-3 p-4 rounded-lg border transition-colors ${coupon.is_active ? 'bg-white' : 'bg-muted/50 opacity-60'} ${selectedCoupons.has(coupon.coupon_id) ? 'ring-2 ring-violet-300 border-violet-300' : ''}`}
                                      data-testid={`coupon-card-${coupon.coupon_id}`}
                                    >
                                      <input
                                        type="checkbox"
                                        className="w-4 h-4 accent-violet-600 cursor-pointer flex-shrink-0"
                                        checked={selectedCoupons.has(coupon.coupon_id)}
                                        onChange={() => toggleSelectCoupon(coupon.coupon_id)}
                                        data-testid={`coupon-select-${coupon.coupon_id}`}
                                      />
                                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                        <Tag className="w-5 h-5 text-primary" />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                                          <span className="font-mono font-bold text-sm">{coupon.code}</span>
                                          <Badge variant="outline" className="text-[10px]">
                                            {formatCouponValue(coupon)}
                                          </Badge>
                                          {!coupon.is_active && <Badge variant="secondary" className="text-[10px]">Inactivo</Badge>}
                                        </div>
                                        <p className="text-xs text-muted-foreground truncate">{coupon.description || 'Sin descripcion'}</p>
                                        <div className="flex gap-3 mt-1 text-[10px] text-muted-foreground flex-wrap">
                                          <span>Usos: {coupon.used_count}{coupon.max_uses ? `/${coupon.max_uses}` : ''}</span>
                                          <span>
                                            Caduca: <strong>{coupon.expires_at ? formatDate(coupon.expires_at) : 'Sin fecha'}</strong>
                                          </span>
                                          {coupon.assigned_to && <span className="text-blue-600">Asignado: {getUserName(coupon.assigned_to)}</span>}
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-1">
                                        <Switch checked={coupon.is_active} onCheckedChange={() => toggleCoupon(coupon)} data-testid={`coupon-toggle-${coupon.coupon_id}`} />
                                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditCoupon(coupon)} data-testid={`coupon-edit-${coupon.coupon_id}`}>
                                          <Pencil className="w-3.5 h-3.5" />
                                        </Button>
                                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700" onClick={() => deleteCoupon(coupon)} data-testid={`coupon-delete-${coupon.coupon_id}`}>
                                          <Trash2 className="w-3.5 h-3.5" />
                                        </Button>
                                      </div>
                                    </div>
                                  );
                                }
                                // Thank-you coupon with reminder calendar.
                                return (
                                  <div
                                    key={coupon.coupon_id}
                                    className={`rounded-lg border transition-colors ${coupon.is_active ? 'bg-white' : 'bg-muted/50 opacity-60'} ${selectedCoupons.has(coupon.coupon_id) ? 'ring-2 ring-violet-300 border-violet-300' : ''}`}
                                    data-testid={`coupon-card-${coupon.coupon_id}`}
                                  >
                                    <div className="flex items-center gap-3 p-4">
                                      <input
                                        type="checkbox"
                                        className="w-4 h-4 accent-violet-600 cursor-pointer flex-shrink-0"
                                        checked={selectedCoupons.has(coupon.coupon_id)}
                                        onChange={() => toggleSelectCoupon(coupon.coupon_id)}
                                        data-testid={`coupon-select-${coupon.coupon_id}`}
                                      />
                                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                                        <Tag className="w-5 h-5 text-amber-700" />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                                          <span className="font-mono font-bold text-sm">{coupon.code}</span>
                                          <Badge variant="outline" className="text-[10px]">
                                            {formatCouponValue(coupon)}
                                          </Badge>
                                          <Badge variant="outline" className="text-[10px] bg-amber-50 border-amber-200 text-amber-800">
                                            Agradecimiento colaborador
                                          </Badge>
                                          {!coupon.is_active && <Badge variant="secondary" className="text-[10px]">Inactivo</Badge>}
                                          {coupon.assigned_to && (
                                            <Badge className="text-[10px] bg-emerald-100 text-emerald-800 border-emerald-300">
                                              Reclamado
                                            </Badge>
                                          )}
                                        </div>
                                        <div className="flex gap-3 mt-1 text-[11px] text-muted-foreground flex-wrap">
                                          <span>
                                            Colaborador: <strong className="text-slate-700">{collaboratorEmail || '—'}</strong>
                                          </span>
                                          <span>
                                            Caduca: <strong className="text-slate-700">{formatDate(coupon.expires_at)}</strong>
                                          </span>
                                          <span>Usos: {coupon.used_count}{coupon.max_uses ? `/${coupon.max_uses}` : ''}</span>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-1">
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-8 gap-1 text-xs"
                                          onClick={() => toggleCouponExpanded(coupon.coupon_id)}
                                          data-testid={`coupon-toggle-reminders-${coupon.coupon_id}`}
                                          title="Ver calendario de recordatorios"
                                        >
                                          <CalendarClock className="w-3.5 h-3.5" />
                                          {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                        </Button>
                                        <Switch checked={coupon.is_active} onCheckedChange={() => toggleCoupon(coupon)} data-testid={`coupon-toggle-${coupon.coupon_id}`} />
                                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditCoupon(coupon)} data-testid={`coupon-edit-${coupon.coupon_id}`}>
                                          <Pencil className="w-3.5 h-3.5" />
                                        </Button>
                                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700" onClick={() => deleteCoupon(coupon)} data-testid={`coupon-delete-${coupon.coupon_id}`}>
                                          <Trash2 className="w-3.5 h-3.5" />
                                        </Button>
                                      </div>
                                    </div>
                                    {isExpanded && (
                                      <div className="px-4 pb-4">
                                        <ReminderSchedule coupon={coupon} />
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                      )}
                    </div>
                  );
                })()
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* === COUPON DIALOG === */}
      <Dialog open={couponDialogOpen} onOpenChange={setCouponDialogOpen}>
        <DialogContent className="max-w-md" data-testid="coupon-dialog">
          <DialogHeader>
            <DialogTitle>{editingCoupon ? 'Editar cupon' : 'Nuevo cupon'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs text-muted-foreground">Codigo</Label>
              <Input
                value={couponForm.code}
                onChange={(e) => setCouponForm(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                placeholder="Ej: VERANO2026"
                className="mt-1 font-mono"
                disabled={!!editingCoupon}
                data-testid="coupon-form-code"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Tipo de descuento</Label>
                <Select value={couponForm.type} onValueChange={(v) => setCouponForm(p => ({ ...p, type: v }))}>
                  <SelectTrigger className="mt-1" data-testid="coupon-form-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COUPON_TYPES.map(ct => <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">
                  {couponForm.type === 'percentage' ? 'Porcentaje (%)' : 'Importe (€)'}
                </Label>
                <Input
                  type="number"
                  value={couponForm.value}
                  onChange={(e) => setCouponForm(p => ({ ...p, value: e.target.value }))}
                  className="mt-1"
                  disabled={couponForm.type === 'free_book' || couponForm.type === 'free_extra'}
                  data-testid="coupon-form-value"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Descripcion</Label>
              <Input
                value={couponForm.description}
                onChange={(e) => setCouponForm(p => ({ ...p, description: e.target.value }))}
                placeholder="Descripcion interna del cupon"
                className="mt-1"
                data-testid="coupon-form-desc"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Usos maximos</Label>
                <Input
                  type="number"
                  value={couponForm.max_uses}
                  onChange={(e) => setCouponForm(p => ({ ...p, max_uses: e.target.value }))}
                  placeholder="Ilimitado"
                  className="mt-1"
                  data-testid="coupon-form-maxuses"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Fecha expiracion</Label>
                <Input
                  type="date"
                  value={couponForm.expires_at}
                  onChange={(e) => setCouponForm(p => ({ ...p, expires_at: e.target.value }))}
                  className="mt-1"
                  data-testid="coupon-form-expires"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Asignar a cliente (opcional)</Label>
              <Select value={couponForm.assigned_to || '_none'} onValueChange={(v) => setCouponForm(p => ({ ...p, assigned_to: v === '_none' ? '' : v }))}>
                <SelectTrigger className="mt-1" data-testid="coupon-form-assigned">
                  <SelectValue placeholder="Publico (todos)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Publico (todos los clientes)</SelectItem>
                  {users.map(u => (
                    <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.email})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Restringir a un email (opcional)</Label>
              <Input
                type="email"
                value={couponForm.assigned_email}
                onChange={(e) => setCouponForm(p => ({ ...p, assigned_email: e.target.value }))}
                placeholder="colaborador@ejemplo.com"
                className="mt-1"
                data-testid="coupon-form-assigned-email"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Si lo rellenas, solo la persona logueada con ese correo podra usar el cupon. Util para enviar codigos personales a colaboradores.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCouponDialogOpen(false)}>Cancelar</Button>
            <Button onClick={saveCoupon} disabled={savingCoupon} className="gap-1" data-testid="coupon-save-btn">
              {savingCoupon ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {editingCoupon ? 'Actualizar' : 'Crear cupon'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === STATUS DIALOG === */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent className="max-w-sm" data-testid="status-dialog">
          <DialogHeader>
            <DialogTitle>Cambiar estado de factura</DialogTitle>
            <DialogDescription>
              Factura: {statusInvoice?.invoice_number}
            </DialogDescription>
          </DialogHeader>
          <Select value={newStatus} onValueChange={setNewStatus}>
            <SelectTrigger data-testid="status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="paid">Pagada</SelectItem>
              <SelectItem value="pending">Pendiente</SelectItem>
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialogOpen(false)}>Cancelar</Button>
            <Button onClick={changeInvoiceStatus} data-testid="status-save-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === EDIT INVOICE NUMBER DIALOG === */}
      <Dialog open={editNumberDialog} onOpenChange={(open) => { if (!open) { setEditNumberDialog(false); setEditingInvoice(null); } }}>
        <DialogContent className="max-w-sm" data-testid="edit-number-dialog">
          <DialogHeader>
            <DialogTitle>Editar numero de factura</DialogTitle>
            <DialogDescription>
              Factura actual: {editingInvoice?.invoice_number}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs text-muted-foreground">Nuevo numero</Label>
            <Input
              value={editNumber}
              onChange={(e) => setEditNumber(e.target.value)}
              className="mt-1 font-mono"
              data-testid="edit-number-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditNumberDialog(false)}>Cancelar</Button>
            <Button onClick={saveInvoiceNumber} disabled={!editNumber.trim()} data-testid="edit-number-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === CREATE INVOICE DIALOG === */}
      <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="create-invoice-dialog">
          <DialogHeader>
            <DialogTitle>Crear factura manual</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto">
            <div>
              <Label className="text-xs text-muted-foreground">Cliente</Label>
              <Select value={invoiceForm.user_id || '_none'} onValueChange={(v) => setInvoiceForm(p => ({ ...p, user_id: v === '_none' ? '' : v }))}>
                <SelectTrigger className="mt-1" data-testid="inv-form-user">
                  <SelectValue placeholder="Seleccionar cliente..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none" disabled>Seleccionar cliente...</SelectItem>
                  {users.map(u => (
                    <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.email})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Tipo</Label>
                <Select value={invoiceForm.invoice_type} onValueChange={(v) => setInvoiceForm(p => ({ ...p, invoice_type: v }))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual</SelectItem>
                    <SelectItem value="initial">Libro</SelectItem>
                    <SelectItem value="extras">Extras</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Estado</Label>
                <Select value={invoiceForm.status} onValueChange={(v) => setInvoiceForm(p => ({ ...p, status: v }))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pendiente</SelectItem>
                    <SelectItem value="paid">Pagada</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Conceptos</Label>
              <div className="space-y-2 mt-1">
                {invoiceForm.items.map((item, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-6">
                      {idx === 0 && <span className="text-[10px] text-muted-foreground">Descripcion</span>}
                      <Input
                        value={item.description}
                        onChange={(e) => {
                          const items = [...invoiceForm.items];
                          items[idx].description = e.target.value;
                          setInvoiceForm(p => ({ ...p, items }));
                        }}
                        placeholder="Concepto"
                        className="h-8 text-sm"
                        data-testid={`inv-item-desc-${idx}`}
                      />
                    </div>
                    <div className="col-span-2">
                      {idx === 0 && <span className="text-[10px] text-muted-foreground">Cant.</span>}
                      <Input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => {
                          const items = [...invoiceForm.items];
                          items[idx].quantity = e.target.value;
                          setInvoiceForm(p => ({ ...p, items }));
                        }}
                        className="h-8 text-sm"
                        min="1"
                      />
                    </div>
                    <div className="col-span-3">
                      {idx === 0 && <span className="text-[10px] text-muted-foreground">Precio ud.</span>}
                      <Input
                        type="number"
                        value={item.unit_price}
                        onChange={(e) => {
                          const items = [...invoiceForm.items];
                          items[idx].unit_price = e.target.value;
                          setInvoiceForm(p => ({ ...p, items }));
                        }}
                        className="h-8 text-sm"
                        step="0.01"
                      />
                    </div>
                    <div className="col-span-1">
                      {idx > 0 && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => {
                          const items = invoiceForm.items.filter((_, i) => i !== idx);
                          setInvoiceForm(p => ({ ...p, items }));
                        }}>
                          <X className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                <Button variant="outline" size="sm" className="text-xs gap-1" onClick={() => {
                  setInvoiceForm(p => ({ ...p, items: [...p.items, { description: '', quantity: 1, unit_price: 0 }] }));
                }} data-testid="inv-add-item">
                  <Plus className="w-3 h-3" /> Anadir concepto
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Notas (opcional)</Label>
              <Input
                value={invoiceForm.notes}
                onChange={(e) => setInvoiceForm(p => ({ ...p, notes: e.target.value }))}
                placeholder="Notas internas..."
                className="mt-1"
              />
            </div>
            {/* Total preview */}
            <div className="p-3 bg-muted/30 rounded-lg text-sm">
              <div className="flex justify-between">
                <span>Subtotal</span>
                <span>{invoiceForm.items.reduce((s, i) => s + (parseInt(i.quantity) || 0) * (parseFloat(i.unit_price) || 0), 0).toFixed(2)}€</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>IVA (21%)</span>
                <span>{(invoiceForm.items.reduce((s, i) => s + (parseInt(i.quantity) || 0) * (parseFloat(i.unit_price) || 0), 0) * 0.21).toFixed(2)}€</span>
              </div>
              <div className="flex justify-between font-bold border-t pt-1 mt-1">
                <span>Total</span>
                <span>{(invoiceForm.items.reduce((s, i) => s + (parseInt(i.quantity) || 0) * (parseFloat(i.unit_price) || 0), 0) * 1.21).toFixed(2)}€</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInvoiceDialogOpen(false)}>Cancelar</Button>
            <Button onClick={createManualInvoice} disabled={savingInvoice} className="gap-1" data-testid="inv-create-btn">
              {savingInvoice ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              Crear factura
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === PDF PREVIEW DIALOG === */}
      <Dialog open={!!previewUrl} onOpenChange={(open) => { if (!open) closePreview(); }}>
        <DialogContent className="max-w-4xl h-[85vh] p-0 overflow-hidden" data-testid="invoice-preview-dialog">
          <DialogHeader className="px-6 pt-5 pb-3 border-b">
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <Receipt className="w-5 h-5 text-primary" />
                Factura {previewNumber}
              </DialogTitle>
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => {
                if (previewUrl) {
                  const link = document.createElement('a');
                  link.href = previewUrl;
                  link.download = `factura_${previewNumber}.pdf`;
                  link.click();
                }
              }} data-testid="preview-download-btn">
                <Download className="w-3.5 h-3.5" /> Descargar
              </Button>
            </div>
          </DialogHeader>
          {previewUrl && (
            <iframe
              src={previewUrl}
              className="w-full flex-1"
              style={{ height: 'calc(85vh - 70px)', border: 'none' }}
              title={`Factura ${previewNumber}`}
              data-testid="invoice-preview-iframe"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
